import {
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowRightLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  CornerDownLeft,
  CornerDownRight,
  CornerUpLeft,
  CornerUpRight,
  ExternalLink,
  Forward,
  ListIndentDecrease,
  ListIndentIncrease,
  ListEnd,
  ListStart,
  LogIn,
  LogOut,
  PanelLeft,
  PanelRight,
  Redo,
  Redo2,
  Reply,
  ReplyAll,
  Send,
  Undo,
  Undo2,
  type LucideIcon,
  type LucideProps,
} from 'lucide-react';
import { cn } from '@shared/lib/cn';

/**
 * Icons that flip in RTL (a11y doc §11.7). Never mirrored: clocks, calendar, media
 * (play/pause/skip), check, x, plus/minus, search, star, book, custom Qur'an icons, brand mark.
 */
export const DIRECTIONAL_ICONS: ReadonlySet<LucideIcon> = new Set<LucideIcon>([
  ArrowLeft,
  ArrowRight,
  ArrowLeftRight,
  ArrowRightLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  CornerDownLeft,
  CornerDownRight,
  CornerUpLeft,
  CornerUpRight,
  Send,
  Reply,
  ReplyAll,
  Forward,
  Undo,
  Undo2,
  Redo,
  Redo2,
  ListIndentIncrease,
  ListIndentDecrease,
  LogIn,
  LogOut,
  ExternalLink,
  ListStart,
  ListEnd,
  PanelLeft,
  PanelRight,
]);

export function isDirectionalIcon(icon: LucideIcon): boolean {
  return DIRECTIONAL_ICONS.has(icon);
}

export interface IconProps extends Omit<LucideProps, 'ref'> {
  icon: LucideIcon;
  /** Force mirroring on/off; defaults to the directional allow-list. */
  mirror?: boolean;
  /** Accessible name. Without it the icon is decorative (aria-hidden). */
  label?: string;
}

/** Lucide icon at 1.5 px stroke (brief §7) with RTL mirroring for directional glyphs. */
export function Icon({ icon: Glyph, mirror, label, className, size = 20, ...rest }: IconProps) {
  const shouldMirror = mirror ?? isDirectionalIcon(Glyph);
  return (
    <Glyph
      size={size}
      strokeWidth={1.5}
      className={cn('shrink-0', shouldMirror && 'icon-directional', className)}
      data-mirrored={shouldMirror ? 'true' : undefined}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? 'img' : undefined}
      focusable="false"
      {...rest}
    />
  );
}
